import { DecryptedService } from '@icure/cardinal-sdk'
import { Version } from '@icure/form/generic'
import { Code, FieldMetadata } from '@icure/form/components/model'
import { normalizeCode } from '@icure/form/utils/code-utils'

export const getRevisionsFilter = (label: string): ((id: string, history: Version<FieldMetadata>[]) => string[]) => {
  return (id, history) =>
    history
      .filter((fmd) => fmd?.value?.label === label)
      .map((fmd) => fmd.revision)
      .filter((r) => r !== undefined) as string[] //null is used as a new revision indicator
}

export const getServicesForLabel = (
  indexedServices: { [p: string]: Version<DecryptedService>[] },
  label: string,
): {
  [id: string]: Version<DecryptedService>[]
} =>
  Object.entries(indexedServices)
    .map(([id, history]) => {
      const keptRevisions = getRevisionsFilter(label)(
        id,
        history.map(({ revision, modified, value: s }) => ({
          revision,
          modified,
          value: {
            label: s.label ?? s.id ?? '',
            owner: s.responsible,
            valueDate: s.valueDate,
            codes: s.codes as unknown as Code[],
            tags: s.tags as unknown as Code[],
          },
        })),
      )
      return [id, history.filter(({ revision }) => revision && keptRevisions.includes(revision))] as [string, Version<DecryptedService>[]]
    })
    .reduce((acc, [id, history]) => (history.length ? { ...acc, [id]: history } : acc), {})

export const getServiceForLabel = (
  indexedServices: {
    [p: string]: Version<DecryptedService>[]
  },
  label: string,
): DecryptedService => {
  const versions = Object.values(getServicesForLabel(indexedServices, label))[0]
  return versions?.[0]?.value
}

export const getAnyNumberContent = (svc: DecryptedService | undefined, language: string): number | undefined => {
  return (
    svc?.content?.[language]?.numberValue ??
    svc?.content?.[language]?.measureValue?.value ??
    svc?.content?.['*']?.numberValue ??
    svc?.content?.['*']?.measureValue?.value ??
    (svc?.content
      ? Object.values(svc.content)
          .map((c) => c.numberValue ?? c.measureValue?.value)
          .filter((v) => v !== null && v !== undefined)[0]
      : undefined)
  )
}

export const getAnyTextContent = (svc: DecryptedService | undefined, language: string): string | undefined => {
  return (
    svc?.content?.[language]?.stringValue ??
    svc?.content?.['*']?.stringValue ??
    (svc?.content
      ? Object.values(svc.content)
          .map((c) => c.stringValue)
          .filter((v) => v !== null && v !== undefined)[0]
      : undefined)
  )
}

export const getCodeNumberValue = (svc: DecryptedService | undefined): number | undefined => {
  const code = svc?.codes?.map((c) => normalizeCode(c)).find((c) => c.type === svc?.label)
  return code?.code ? Number(code.code) : undefined
}

export const svcMeasure = (language: string, service: DecryptedService): string => {
  const measure =
    service?.content?.[language]?.measureValue ??
    service?.content?.['*']?.measureValue ??
    (service?.content
      ? Object.values(service.content)
          .map((c) => c.measureValue)
          .filter((v) => !!v)[0]
      : undefined)
  const decimal =
    service?.content?.[language]?.numberValue ??
    service?.content?.['*']?.numberValue ??
    (service?.content
      ? Object.values(service.content)
          .map((c) => c.numberValue)
          .filter((v) => !!v)[0]
      : undefined)
  return measure ? `${measure.value?.toFixed(2)} ${measure.unit}` : decimal !== null && decimal !== undefined ? `${decimal?.toFixed(2)}` : '-'
}

export const svcText = (
  language: string,
  service: DecryptedService | undefined,
  labelToText: (label: string) => string | undefined = (x) => x,
  optionToText: (label: string) => string | undefined = (x) => x,
): string => {
  if (!service) {
    return '-'
  }
  const text =
    service?.content?.[language]?.stringValue ??
    service?.content?.['*']?.stringValue ??
    (service?.content
      ? Object.values(service.content)
          .map((c) => c.stringValue)
          .filter((v) => !!v)[0]
      : undefined)

  const booleanLabel =
    service?.label &&
    (service?.content?.[language]?.booleanValue ??
      service?.content?.['*']?.booleanValue ??
      (service?.content ? Object.values(service.content).every((c) => !!c.booleanValue) : undefined))
      ? labelToText(service.label)
      : undefined

  const optionLabel = (
    service?.content?.[language]?.compoundValue ??
    service?.content?.['*']?.compoundValue ??
    (service?.content ? Object.values(service.content).flatMap((c) => c.compoundValue ?? []) : [])
  )
    .map((c) => (c.label && c.content && Object.values(c.content).every((c) => !!c.booleanValue) ? optionToText(c.label) : undefined))
    .filter((v) => !!v)
    .join(', ')

  return text ?? booleanLabel ?? (optionLabel.length ? optionLabel : undefined) ?? '-'
}
